/** HTTP status the backend returns for a locked account. */
export const ACCOUNT_LOCKED_STATUS = 422;

/**
 * Account-locked detection, centralized so the same rule is used by the base
 * query (toast suppression), useAuth, and the login screens.
 *
 * A response counts as locked only when BOTH hold:
 *  - the HTTP status is 422 (the backend's locked-account status), AND
 *  - the message contains the lock wording ("locked"/"exceeded").
 *
 * Requiring both avoids treating an unrelated 422 (or an unrelated message that
 * merely contains "locked") as a locked account. Pass either an RTK-Query error
 * object (`res.error`, status at `error.status`) or the base query's
 * `{ status, data }` shape.
 *
 * TODO: the message match is wording/locale-dependent and brittle. When the API
 * exposes a stable machine-readable code for locked accounts (e.g.
 * `data.code === "ACCOUNT_LOCKED"`), key off that instead of scanning the human
 * message, and keep the 422 status gate as a secondary guard.
 */
export const isAccountLockedError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const err = error as {
    status?: unknown;
    message?: unknown;
    data?: { status?: unknown; message?: unknown } | null;
  };

  // Status must be 422 AND the message must match — both are required. A lock-worded
  // message under a different status (or a non-lock message under 422) is NOT a lock.
  //
  // All current callers supply a numeric top-level HTTP status: the base query throws
  // the fetch Response (numeric `.status`) and constructs `res.error` as
  // `{ status: number, data }`, so `err.status` is always the numeric HTTP status here.
  // `Number()` also accepts a stringified "422"; RTK-Query string statuses such as
  // "FETCH_ERROR" become NaN and correctly fail the check. If a future caller passes a
  // thrown error with no status, this returns false by design — locked requires 422.
  const status = Number(err.status ?? err.data?.status);
  if (status !== ACCOUNT_LOCKED_STATUS) return false;

  const message = String(err.data?.message ?? err.message ?? "").toLowerCase();
  return message.includes("locked") || message.includes("exceeded");
};
